/**
 * useVAD — Voice Activity Detection via Silero VAD (@ricky0123/vad-web).
 *
 * Detecte automatiquement quand l'agent a commence et termine de parler.
 * Robuste au bruit voiture (ML model ONNX). Assets self-hostes sous /vad/
 * (copies depuis node_modules au build via vite-plugin-static-copy) — pas
 * de dependance a un CDN tiers (jsDelivr posait failed-to-fetch en prod).
 *
 * Pipeline :
 *  1. Init : getUserMedia + chargement Silero (~2 Mo, cache navigateur).
 *  2. Detection continue : speechStart / speechEnd avec audio Float32Array.
 *  3. Anti-feedback : pauseMic() pendant que Juliette parle (TTS),
 *     resumeMic() quand elle a fini (audio.ended).
 *
 * Compat Safari iOS PWA : Web Audio API + AudioWorklet supportes depuis iOS 14.5.
 * Au premier tap, le user gesture deverrouille AudioContext.
 */

import { useCallback, useEffect, useRef, useState } from "react";

// Assets self-hostes via vite-plugin-static-copy (cf. vite.config.ts).
// Tout est servi sous /vad/ : worklet + .onnx + .wasm + .mjs onnxruntime.
const VAD_BASE_ASSET = "/vad/";
const ORT_WASM_BASE = "/vad/";

export type VADStatus = "idle" | "loading" | "ready" | "listening" | "speaking" | "error";

interface UseVADOptions {
  onSpeechStart?: () => void;
  /** Audio Float32Array (16 kHz mono PCM) capture entre speechStart et speechEnd. */
  onSpeechEnd?: (audio: Float32Array) => void;
  /** Faux positifs : duree min de parole detectee, sous laquelle on ignore. */
  onVADMisfire?: () => void;
  onError?: (error: Error) => void;
}

interface UseVADResult {
  status: VADStatus;
  error: Error | null;
  start: () => Promise<void>;
  stop: () => void;
  /** Coupe le mic temporairement (anti-feedback pendant TTS). */
  pauseMic: () => void;
  /** Reactive le mic apres TTS. */
  resumeMic: () => void;
}

// Type opaque pour MicVAD (charge dynamiquement, evite d'avoir la lib dans
// le bundle initial — economie ~50 ko de code applicatif).
type MicVADInstance = {
  start: () => void;
  pause: () => void;
  destroy: () => void;
};

export function useVAD(opts: UseVADOptions = {}): UseVADResult {
  const [status, setStatus] = useState<VADStatus>("idle");
  const [error, setError] = useState<Error | null>(null);
  const vadRef = useRef<MicVADInstance | null>(null);
  const optsRef = useRef(opts);

  // Garder la derniere version des callbacks sans recreer le VAD.
  useEffect(() => {
    optsRef.current = opts;
  }, [opts]);

  const start = useCallback(async () => {
    if (vadRef.current) {
      vadRef.current.start();
      setStatus("listening");
      return;
    }

    setStatus("loading");
    setError(null);

    try {
      // Lazy import — n'embarque pas la lib dans le bundle initial.
      const { MicVAD } = await import("@ricky0123/vad-web");

      const instance = await MicVAD.new({
        baseAssetPath: VAD_BASE_ASSET,
        onnxWASMBasePath: ORT_WASM_BASE,
        // Seuils durcis (au-dessus des defauts Silero ~0.5 / 0.35) pour reduire les
        // faux declenchements sur bruit ambiant : c'est la 1re source des tours
        // fantomes (le STT hallucine ensuite sur ces micro-declenchements de silence).
        // minSpeechMs : un segment de "parole" < 250 ms est ignore (bruit court).
        positiveSpeechThreshold: 0.6,
        negativeSpeechThreshold: 0.4,
        minSpeechMs: 250,
        onSpeechStart: () => {
          setStatus("speaking");
          optsRef.current.onSpeechStart?.();
        },
        onSpeechEnd: (audio: Float32Array) => {
          setStatus("listening");
          optsRef.current.onSpeechEnd?.(audio);
        },
        onVADMisfire: () => {
          setStatus("listening");
          optsRef.current.onVADMisfire?.();
        },
      });

      vadRef.current = instance as unknown as MicVADInstance;
      instance.start();
      setStatus("listening");
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      setStatus("error");
      optsRef.current.onError?.(e);
    }
  }, []);

  const stop = useCallback(() => {
    if (vadRef.current) {
      vadRef.current.destroy();
      vadRef.current = null;
    }
    setStatus("idle");
  }, []);

  const pauseMic = useCallback(() => {
    vadRef.current?.pause();
  }, []);

  const resumeMic = useCallback(() => {
    vadRef.current?.start();
  }, []);

  // Cleanup au demontage
  useEffect(() => {
    return () => {
      vadRef.current?.destroy();
      vadRef.current = null;
    };
  }, []);

  return { status, error, start, stop, pauseMic, resumeMic };
}
